import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/billing — plan + payment surface.
 *
 * There is no subscription or billing backend yet, so every account is on
 * the Free plan and there are no invoices to show. Paid plans are marked
 * "coming soon" rather than offering upgrades that can't be fulfilled.
 */

const EASE = [0.22, 1, 0.36, 1];

const FREE_PLAN = {
  name: 'Free',
  description: 'Chat with menus and look up allergens at no cost.',
  features: ['Chat with any menu', 'Save your dietary restrictions', 'Safety signals (read)'],
};

export default function UnifiedBilling() {
  const navigate = useNavigate();
  const { userId, isInitialized } = useContext(UserContext);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) navigate('/unified');
  }, [userId, isInitialized, navigate]);

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <CardIcon size={12} /> BILLING
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            Plan &amp; <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>payments</em>.
          </h1>
          <p style={{ maxWidth: 520, margin: '0 auto', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            ChatWithMenu is free to use today.
          </p>
        </motion.section>

        {/* Current plan card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
          style={{
            position: 'relative', overflow: 'hidden',
            padding: '24px 26px', borderRadius: 26,
            background: 'linear-gradient(160deg, #F7FBFF 0%, #E5F3FF 100%)',
            color: 'var(--cwm-ink)',
            boxShadow: '0 8px 24px rgba(13,71,161,0.08)',
            marginBottom: 22,
          }}>
          <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 16, alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                CURRENT PLAN
              </div>
              <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1 }}>
                {FREE_PLAN.name}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6, opacity: 0.92 }}>
                {FREE_PLAN.description}
              </div>
              <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'grid', gap: 5 }}>
                {FREE_PLAN.features.map((f) => (
                  <li key={f} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cwm-gray-700)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ color: 'var(--cwm-blue-deep)', fontWeight: 900, flexShrink: 0, marginTop: 1 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em' }}>$0</span>
                <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.8 }}>/mo</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Paid plans — not available yet */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36, delay: 0.14, ease: EASE }}
          className="cwm-sheet"
          style={{ padding: 22, marginBottom: 22 }}
        >
          <div className="cwm-eyebrow" style={{ marginBottom: 6 }}>PAID PLANS</div>
          <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 6 }}>
            Paid plans coming soon
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-700)', lineHeight: 1.5 }}>
            You won't be charged for anything today. We'll let you know before any paid plan launches.
          </div>
        </motion.div>

        {/* Invoices */}
        <div>
          <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>INVOICES</div>
          <div className="cwm-sheet" style={{ padding: '28px 20px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
            No invoices — you're on the Free plan.
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function CardIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18M7 14h3"/></svg>; }
